<script setup lang="ts">
import { provide } from "vue";
// Relative (not @/) imports: App.vue is MF-exposed as PageFrame; its emitted
// d.ts ships to consumers where the producer's @/ alias can't resolve.
import SideNav from "./features/layout/SideNav.vue";
import AdminBar from "./features/layout/AdminBar.vue";
import NavToggle from "./features/layout/NavToggle.vue";
import Toast from "primevue/toast";
import ConfirmDialog from "primevue/confirmdialog";
import { useSideNav } from "./features/layout/useSideNav";
import { NAV_TOGGLE_KEY } from "./features/layout/nav-toggle";
import type { MenuItem } from "./features/layout/menu";
import styles from "./App.module.css";

// Also consumed cross-MF as the layout expose's PageFrame; menu is
// overridable there while this host itself renders the default.
defineProps<{ menuItems?: MenuItem[] }>();

const { isPinned } = useSideNav();

// COMPONENT-level provide, deliberately not app.provide(): hosted mode would be
// fine either way, but in a remote's standalone harness the remote owns the app
// instance and this frame is only a descendant of it, so an app-level provide on
// the host side would not exist. Providing here covers both — every page renders
// inside our RouterView.
provide(NAV_TOGGLE_KEY, NavToggle);
</script>

<template>
  <div :class="styles.frame">
    <AdminBar />
    <div :class="[styles.shell, isPinned && styles.pinned]">
      <Toast />
      <ConfirmDialog />
      <SideNav :menu-items="menuItems" />
      <main :class="styles.content">
        <!-- Keyed by legacy remote: two legacy prefixes render the same mount
             component, and a prefix switch must remount it, not patch it. -->
        <RouterView v-slot="{ Component, route }">
          <component :is="Component" :key="route.meta.legacyRemoteId" />
        </RouterView>
      </main>
    </div>
  </div>
</template>
